import { useState, useCallback, useMemo } from 'react';
import { getProfile } from '../services/vendorAPI';

// Utility function to remove undefined values from objects
const removeUndefinedValues = (obj) => {
  if (obj === null || obj === undefined) return obj;

  // Handle primitive types
  if (typeof obj !== 'object') return obj;

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.filter(item => item !== undefined);
  }

  // Handle objects - only clean top level undefined values
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

const useDocument = collectionName => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch a single document by document ID
  const fetchDocument = useCallback(async docId => {
    try {
      setLoading(true);
      // For now, this is mainly used for profile fetching
      // If collectionName is 'merchants', fetch profile
      if (collectionName === 'merchants') {
        const response = await getProfile();
        if (response.success) {
          setLoading(false);
          return { result: true, id: response.data.id, ...response.data };
        } else {
          setLoading(false);
          return { result: false };
        }
      }
      setLoading(false);
      return { result: false };
    } catch (err) {
      console.log(err);
      setLoading(false);
      setError(err);
      return { result: false };
    }
  }, [collectionName]);

  const fetchUserById = useCallback(async uid => {
    try {
      // Fetch profile using the API
      const response = await getProfile();
      if (response.success) {
        let userData = response.data;
        // Unwrap array if needed
        if (Array.isArray(userData)) {
          userData = userData[0];
        }
        // Unwrap nested data if needed
        else if (userData && userData.data && !userData.id) {
          userData = Array.isArray(userData.data) ? userData.data[0] : userData.data;
        }

        // Check if the ID matches (if uid is provided)
        if (!uid || (userData && (userData.id === uid || userData.id?.toString() === uid?.toString()))) {
          return { success: true, data: userData };
        } else {
          return { success: false, message: 'Document not found' };
        }
      } else {
        return { success: false, message: 'Document not found' };
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Generic collection fetch - not used much with new API structure
  const fetchCollection = useCallback(async collName => {
    try {
      setLoading(true);
      // This would need to be mapped to appropriate API endpoints
      // For now, return empty
      setLoading(false);
      return { result: false, data: [] };
    } catch (err) {
      console.log(err);
      setLoading(false);
      setError(err);
    }
  }, []);

  // Legacy function - kept for compatibility
  const getDocByRef = useCallback(async docRef => {
    try {
      // This was used for Firebase document references
      // With new API, we'll fetch profile instead
      const response = await getProfile();
      if (response.success) {
        return {
          id: response.data.id,
          ...response.data,
        };
      } else {
        console.log('Document does not exist');
        return null;
      }
    } catch (error) {
      console.error('Error in getDocByRef:', error);
      throw error;
    }
  }, []);

  // These functions are kept for compatibility but may need API-specific implementations
  const createDocument = useCallback(async (data, docId = null) => {
    try {
      // Clean the data by removing undefined values
      const cleanedData = removeUndefinedValues(data);
      // This would need to be mapped to appropriate API create endpoints
      // For now, return success: false
      return { success: false, error: 'Create document not implemented for this collection' };
    } catch (err) {
      console.error(err);
      setError(err);
      return { success: false };
    }
  }, []);

  const updateDocument = useCallback(async (docId, data) => {
    try {
      // Clean the data by removing undefined values
      const cleanedData = removeUndefinedValues(data);
      // This would need to be mapped to appropriate API update endpoints
      // For now, return success: false
      return { success: false, error: 'Update document not implemented for this collection' };
    } catch (err) {
      console.log(err);
      setError(err);
      return { success: false };
    }
  }, []);

  const deleteDocument = useCallback(async (docId) => {
    try {
      // This would need to be mapped to appropriate API delete endpoints
      // For now, return success: false
      return { success: false, error: 'Delete document not implemented for this collection' };
    } catch (err) {
      console.error(err);
      setError(err);
      return { success: false };
    }
  }, []);

  return useMemo(() => ({
    loading,
    error,
    fetchDocument,
    createDocument,
    fetchUserById,
    updateDocument,
    fetchCollection,
    getDocByRef,
    deleteDocument,
  }), [loading, error, fetchDocument, createDocument, fetchUserById, updateDocument, fetchCollection, getDocByRef, deleteDocument]);
};

export default useDocument;
